import React, { useMemo, useState } from 'react';
import {
  Box, Button, Dialog, DialogContent, IconButton, TextField, InputAdornment, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CardGiftcardOutlinedIcon from '@mui/icons-material/CardGiftcardOutlined';
import LocalText from '../common/LocalText';
import ConfirmModal from '../form-components/ConfirmModal';
import { C, btnBase, ctaNavy } from '../../theme/adminTokens';

// Both catalog sources merged into `catalog` (getItemPicker + getPrizeCatalog) are lean,
// flat shapes ({ itemId, itemTypeId, name, thumbnailUrl? }) — no nested
// merchandise/ticket/badge/avatar sub-objects. Prize-only items (getPrizeCatalog) resolve
// thumbnailUrl via sp_Item_SelectPrizeOnlyBySpace's own OUTER APPLY, same as the full picker.
const getItemImage = (item) => item?.thumbnailUrl || null;

// Same thin-scrollbar treatment as AnnouncementDetailModal.jsx's tables — the native OS
// scrollbar on this grid's own overflow looked out of place against the rest of the
// redesigned Game Announcement + Prize Grant flow.
const thinScrollbarSx = {
  '&::-webkit-scrollbar': { width: 6, height: 6 },
  '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
  '&::-webkit-scrollbar-thumb': { backgroundColor: C.border, borderRadius: 4 },
  '&::-webkit-scrollbar-thumb:hover': { backgroundColor: C.muted },
  scrollbarWidth: 'thin',
  scrollbarColor: `${C.border} transparent`,
};

const itemLabel = (item) => item?.name || `Item #${item?.itemId}`;

function PickerCard({ item, itemTypes, added, onToggle }) {
  const imageUrl = getItemImage(item);
  const [imgError, setImgError] = useState(false);
  const typeLabel = itemTypes.find((t) => t.id === item.itemTypeId)?.Type ?? '—';

  return (
    <Box
      sx={{
        bgcolor: 'white',
        borderRadius: '12px',
        border: `1px solid ${added ? '#059669' : 'rgba(0,0,0,0.07)'}`,
        boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ position: 'relative', height: 110, bgcolor: '#e2e8f0', overflow: 'hidden', flexShrink: 0 }}>
        {imageUrl && !imgError ? (
          <Box
            component="img"
            src={imageUrl}
            alt={itemLabel(item)}
            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
            onError={() => setImgError(true)}
          />
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <CardGiftcardOutlinedIcon sx={{ fontSize: 32, color: '#94a3b8' }} />
          </Box>
        )}
        <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(0,0,0,0.45)', p: 0.75 }}>
          <Box sx={{ display: 'inline-block', px: 1, py: 0.25, borderRadius: '6px', bgcolor: 'rgba(59,130,246,0.8)', fontSize: 9, fontWeight: 700, color: 'white' }}>
            {typeLabel}
          </Box>
        </Box>
      </Box>

      <Box sx={{ p: 1.25, flex: 1 }}>
        <Typography fontWeight={700} fontSize={12.5} sx={{ color: '#1e293b', lineHeight: 1.35 }}>
          {itemLabel(item)}
        </Typography>
      </Box>

      <Box sx={{ px: 1.25, py: 1, borderTop: '1px solid #f1f5f9' }}>
        <Button
          fullWidth
          size="small"
          variant={added ? 'contained' : 'outlined'}
          startIcon={added ? <CheckIcon sx={{ fontSize: 13 }} /> : <AddIcon sx={{ fontSize: 13 }} />}
          onClick={() => onToggle(item.itemId)}
          sx={added
            ? { bgcolor: '#059669', color: 'white', fontWeight: 700, fontSize: 11, textTransform: 'none', borderRadius: '8px', py: 0.5, boxShadow: 'none', '&:hover': { bgcolor: '#047857' } }
            : { color: C.navy, borderColor: C.border, fontWeight: 700, fontSize: 11, textTransform: 'none', borderRadius: '8px', py: 0.5, '&:hover': { borderColor: C.navy, bgcolor: '#eff6ff' } }}
        >
          <LocalText>{added ? 'Added' : 'Add'}</LocalText>
        </Button>
      </Box>
    </Box>
  );
}

/**
 * "Select Item" popup for the announcement's prize pack picker — a search bar + grid of
 * thumbnail cards, same visual language as PackageAddItems.jsx's "Select Item for Package"
 * screen (PackageItemCard), just as a popup instead of a full page since this lives inside
 * another modal. Multi-select: stays open, each card toggles Add/Added independently.
 */
export default function PrizeItemPickerModal({
  open, onClose, catalog, itemTypes = [], selectedIds, onToggle, onCreatePriceConfirmed,
  allowCreatePrice = true,
}) {
  const [search, setSearch] = useState('');
  const [confirmLeaveOpen, setConfirmLeaveOpen] = useState(false);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return catalog;
    return catalog.filter((item) => itemLabel(item).toLowerCase().includes(q));
  }, [catalog, search]);

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: '16px', border: `1px solid ${C.border}` } }}
      >
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography sx={{ fontSize: 15, fontWeight: 800, color: '#1e293b' }}>
              <LocalText>Select Item</LocalText>
            </Typography>
            <IconButton onClick={onClose} size="small" aria-label="Close">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
            <TextField
              size="small"
              placeholder="Search items…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment> }}
              sx={{ flex: 1, minWidth: 200, '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
            />
            {allowCreatePrice && (
              <Button
                variant="outlined"
                endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                onClick={() => setConfirmLeaveOpen(true)}
                sx={{ ...btnBase, borderColor: C.border, color: C.navy, '&:hover': { borderColor: C.navy, bgcolor: '#eff6ff' } }}
              >
                <LocalText>Create Prize Item</LocalText>
              </Button>
            )}
          </Box>

          {filtered.length === 0 ? (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <Typography fontSize={12.5} sx={{ color: '#94a3b8' }}>
                <LocalText>{catalog.length === 0 ? 'No items available yet.' : 'No items match your search.'}</LocalText>
              </Typography>
            </Box>
          ) : (
            <Box sx={{
              display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 1.5,
              maxHeight: 420, overflowY: 'auto', pr: 0.5,
              ...thinScrollbarSx,
            }}
            >
              {filtered.map((item) => (
                <PickerCard
                  key={item.itemId}
                  item={item}
                  itemTypes={itemTypes}
                  added={selectedIds.has(item.itemId)}
                  onToggle={onToggle}
                />
              ))}
            </Box>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2.5 }}>
            <Button variant="contained" onClick={onClose} sx={{ ...btnBase, ...ctaNavy }}>
              <LocalText>Done</LocalText>
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      <ConfirmModal
        open={confirmLeaveOpen}
        onCancel={() => setConfirmLeaveOpen(false)}
        onConfirm={() => { setConfirmLeaveOpen(false); onCreatePriceConfirmed(); }}
        severity="warning"
        title="Leave and create a new price item?"
        message="Your announcement progress (dates, top count, selected prizes) will be lost."
        confirmLabel="Yes, Continue"
      />
    </>
  );
}
